import React, { useEffect, useRef, useState } from 'react';
import { useScrollPosition } from './useScrollPosition';
import './ContentMenu.styles.scss';

interface MenuItem {
  id: string;
  text: string;
  level: number;
  offset: number;
}

export interface ContentMenuProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  hideMenu: () => void;
  /** The application's current pathname, supplied via router hook or parent component.
   * This value ensures the DOM scanning effect reruns on each page load.
   */
  pathname: string;
  /** A string title or custom React Element that sits above the ToC links
   * @default "Content"
   */
  title?: string | React.ReactElement;
  /** If true, links will be indented according to the heading tag
   * @default true
   */
  indent?: boolean;
  /** A number (in pixels) that sets the indent padding for each nested heading.
   * @default 10
   */
  indentSize?: number;
  /** Callback function that tells the parent how many valid headings are on the page */
  headingCount: (count: number) => void;
}

/**
 * The `TableofContents` component queries the DOM for all heading tags that
 * have an ID. It then creates an indented / scroll activated list of heading links.
 */
export const ContentMenu = ({
  hideMenu,
  indent = true,
  indentSize = 10,
  className,
  pathname,
  headingCount,
}: ContentMenuProps) => {
  const [menuItems, setMenu] = useState<MenuItem[]>([]);
  const [activeNode, setActiveNode] = useState<number | null>(null);
  const [initialRender, setInitialRender] = useState(true);

  const headingRefs = useRef<(HTMLElement | null)[]>([]);

  const findHeadings = () => {
    const allHeadings: MenuItem[] = [];
    headingRefs.current = [];

    const foundHeadings = Array.from(
      document.querySelectorAll<HTMLElement>('h2, h3')
    );

    foundHeadings.forEach((heading, index) => {
      const id =
        heading.id || heading.textContent?.replace(/\s/g, '-').toLowerCase();
      if (id) {
        heading.id = id;
        headingRefs.current[index] = heading;
        allHeadings.push({
          id,
          text: getFirstSpanText(
            !heading?.innerHTML.includes('<!--')
              ? heading?.innerHTML
              : heading?.innerText
          ) as string,
          level: heading.tagName === 'H2' ? 2 : 3,
          offset: heading.offsetTop,
        });
      }
    });

    setMenu(allHeadings);
  };

  /**
   * Query DOM for applicable heading elements
   */
  useEffect(() => {
    findHeadings();
  }, [pathname, initialRender]);

  // Handle Scroll position
  useScrollPosition(
    ({ currPos, prevPos }) => {
      const isDownScroll = currPos > prevPos;

      /**
       * Reset activeNode if scroll position is above first selector
       */
      if (activeNode !== undefined && currPos < menuItems[0]?.offset) {
        return setActiveNode(null);
      }

      if (currPos >= menuItems[0].offset) {
        if (activeNode === null) {
          /**
           * Check if scroll is between first 2 heading nodes
           */
          if (menuItems.length > 1 && currPos <= menuItems[1]?.offset) {
            return setActiveNode(0);
          } else {
            /**
             * Else find the nearest offset
             * Used for fresh page loads if scroll is not at top of document
             */
            const offsets = menuItems.map((i) => i.offset);
            const closest = offsets.reduce((a, b) => {
              return Math.abs(b - currPos) < Math.abs(a - currPos) ? b : a;
            });
            return setActiveNode(
              menuItems.findIndex((i) => i.offset === closest)
            );
          }
        }

        if (isDownScroll) {
          /**
           * If scrolling down, compare current node offset with the next offset
           */
          if (
            activeNode !== menuItems.length - 1 &&
            currPos >= menuItems[activeNode + 1].offset
          ) {
            return setActiveNode(activeNode + 1);
          }
        } else {
          /**
           * If scrolling up, compare current node offset with previous offset
           */
          if (currPos <= menuItems[activeNode]?.offset) {
            return setActiveNode(activeNode - 1);
          }
        }
      }
    },
    200,
    menuItems.length ? true : false
  );

  useEffect(() => {
    if (!menuItems) return;
    headingCount(menuItems.length);
  }, [menuItems, headingCount]);

  useEffect(() => {
    setTimeout(() => {
      setInitialRender(false);
    }, 100);
  }, []);

  return (
    <div className={`toc ${className}`}>
      <div className="flex align-center toc-title">
        <span>Page Content</span>
        <button className="toc-button" onClick={() => hideMenu()}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
            <path strokeWidth="2" fill="none" d="M14 6 L8 12 L2 6" />
          </svg>
        </button>
      </div>
      <ul className="toc-headings">
        {menuItems.length
          ? menuItems.map(({ id, text, level }: MenuItem, index) => (
              <li
                key={index}
                className={`level-${level}`}
                style={{
                  paddingLeft: indent ? indentSize * level : undefined,
                }}>
                <a
                  className={activeNode === index ? 'active' : undefined}
                  onClick={() => setActiveNode(index)}
                  href={`#${id}`}>
                  {text}
                </a>
              </li>
            ))
          : null}
      </ul>
    </div>
  );
};

ContentMenu.displayName = 'ContentMenu';

/**
 * Extract the heading text from a string that may include multiple HTML tags
 *
 * @param text a string that may include HTML tags
 * @return the inner text of the first span tag
 */
function getFirstSpanText(text: string) {
  const spanRegex = /<span>(.*?)<\/span>/i;
  const match = text.match(spanRegex);

  return match ? match[1].replace(/<\/?[^>]+(>|$)/g, '') : null;
}
